import { useCallback, useMemo } from "react";

import { ColumnWidthBounds } from "ds/components/TableSimple/Head";
import { TreeNode } from "utils/tree";
import { SpaceWithParentAccessLevel } from "views/Account/Spaces/types";

import { CELL_PADDING_PX, LEVEL_MARGIN_PX, TREE_RESERVED_CONTENT_WIDTH_PX } from "../constants";
import { getRequiredIndentSteps } from "./indent";

// How narrow the tree column may get depends on the rows rather than on the column: it carries the
// indentation, which grows with every expanded level. A deep tree grows the column past the viewport
// instead of shrinking the indent, and the table scrolls.
const useSpacesListTreeColumnBounds = (
  nodes: Array<TreeNode<SpaceWithParentAccessLevel>>
): ColumnWidthBounds => {
  const deepestIndentSteps = useMemo(
    () => nodes.reduce((deepest, node) => Math.max(deepest, getRequiredIndentSteps(node)), 0),
    [nodes]
  );

  return useCallback(
    (column, index) => {
      const isTreeColumn = index === 0;

      if (!isTreeColumn) {
        return undefined;
      }

      const widthForDeepestIndent =
        LEVEL_MARGIN_PX * deepestIndentSteps + TREE_RESERVED_CONTENT_WIDTH_PX + CELL_PADDING_PX * 2;
      const minWidth = Math.max(column.minWidth, widthForDeepestIndent);

      return {
        minWidth,
        maxWidth: Math.max(column.maxWidth, minWidth),
      };
    },
    [deepestIndentSteps]
  );
};

export default useSpacesListTreeColumnBounds;
